/**
 * Owner: Masa Bayu
 * File: marketplace-taxonomy.ts
 * Date:Time: 2026-09-15 21:01
 * Description: T2 categories / collections taxonomy indexes (presentation only).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertValidId } from './guards';
import { createQxreadBookCard } from './marketplace-book-card';
export const QXREAD_TAXONOMY_KINDS = ['category', 'collection'];
export function createQxreadTaxonomyNode(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead taxonomy node');
    if (!QXREAD_TAXONOMY_KINDS.includes(options.kind)) {
        throw new Error(`QXRead taxonomy kind must be one of: ${QXREAD_TAXONOMY_KINDS.join(', ')}`);
    }
    const slug = assertOptionalTrimmed(options.slug, 'QXRead taxonomy slug');
    const label = assertOptionalTrimmed(options.label, 'QXRead taxonomy label');
    if (slug === null || label === null) {
        throw new Error('QXRead taxonomy slug and label are required');
    }
    const taxonomyId = options.taxonomyId !== undefined ? options.taxonomyId : createQxreadId('qxrdtax');
    assertValidId(taxonomyId, 'QXRead taxonomyId');
    const publicationIds = Object.freeze((options.publicationIds ?? []).map((id, index) => {
        assertValidId(id, `QXRead taxonomy publicationIds[${String(index)}]`);
        return id;
    }));
    return Object.freeze({
        taxonomyId,
        kind: options.kind,
        slug,
        label,
        publicationIds,
    });
}
export function createQxreadTaxonomyIndex(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead taxonomy index');
    for (const node of options.categories ?? []) {
        if (node.kind !== 'category') {
            throw new Error('QXRead taxonomy index categories must use kind "category"');
        }
    }
    for (const node of options.collections ?? []) {
        if (node.kind !== 'collection') {
            throw new Error('QXRead taxonomy index collections must use kind "collection"');
        }
    }
    return Object.freeze({
        categories: Object.freeze([...(options.categories ?? [])]),
        collections: Object.freeze([...(options.collections ?? [])]),
    });
}
/** Derive category nodes from catalog tags — public metadata only. */
export function deriveCategoriesFromCatalog(catalog) {
    const byTag = new Map();
    for (const entry of catalog) {
        if (!entry.marketplaceReady) {
            continue;
        }
        const tags = Array.isArray(entry.metadata.tags) ? entry.metadata.tags : [];
        for (const tag of tags) {
            if (typeof tag !== 'string' || tag.trim().length === 0) {
                continue;
            }
            const slug = tag.trim().toLowerCase();
            const existing = byTag.get(slug) ?? [];
            if (!existing.includes(entry.publicationId)) {
                existing.push(entry.publicationId);
            }
            byTag.set(slug, existing);
        }
    }
    return Object.freeze([...byTag.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([slug, publicationIds]) => createQxreadTaxonomyNode({
        taxonomyId: `qxrdtax_cat_${slug}`,
        kind: 'category',
        slug,
        label: slug.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
        publicationIds,
    })));
}
export function findTaxonomyNodeBySlug(index, kind, slug) {
    const list = kind === 'category' ? index.categories : index.collections;
    for (const node of list) {
        if (node.slug === slug) {
            return node;
        }
    }
    return null;
}
export function resolveTaxonomySurface(catalog, node) {
    const idSet = new Set(node.publicationIds);
    const cards = Object.freeze(catalog
        .filter((entry) => entry.marketplaceReady && idSet.has(entry.publicationId))
        .map((entry) => createQxreadBookCard(entry)));
    return Object.freeze({
        node,
        cards,
        empty: cards.length === 0,
    });
}
/** Taxonomy never implies ownership, entitlement, or Reader launch. */
export function isTaxonomyPresentationOnly(node) {
    return QXREAD_TAXONOMY_KINDS.includes(node.kind);
}
//# sourceMappingURL=marketplace-taxonomy.js.map